import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ICONS, PATHS, IntroContent } from 'src/common/constants';
import Actions from '../Actions';
import AssetForm from '../AssetForm';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import Search from '../Search';
import {
  setAssetFormData,
  clearAssetFormData,
} from 'src/store/actions/assetForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import Row from '../Row';
import AreaDocument from './AreaDocument';
import { variables } from 'src/Tenants';
import ContentBlock from '../ContentBlock';
import './AreaDocument.scss';

interface RoomsAndAssetsProps {
  rooms: any[];
  room: string;
  viewAsset: string;
  viewAssetId: string;
  user: any;
  onDeleteAsset: (areaId: string, assetId: string) => void;
  history: any;
  suite: any;
  property: any;
  dispatchSetAssetFormData: (data: any) => void;
  setShowOverlay: (show: boolean) => void;
  setNewAsset: (isNew: boolean) => void;
}

const RoomsAndAssets = ({
  rooms,
  room,
  viewAsset,
  viewAssetId,
  user,
  onDeleteAsset,
  history,
  suite,
  property,
  dispatchSetAssetFormData,
  setShowOverlay,
  setNewAsset,
}: RoomsAndAssetsProps) => {
  if (viewAsset !== room) return null;

  const filterRooms: any[] = rooms.filter((a: any) => a.item !== null);

  let processedRooms: any[] = rooms;

  if (filterRooms.length > 0) {
    // First create a map of unique items by name.
    processedRooms = [
      ...new Map(
        filterRooms.map((item: any) => [item.item.name, item])
      ).values(),
    ];

    // Then sort the items alphabetically by name
    processedRooms.sort((a: any, b: any) => {
      const nameA = a.item?.name?.toLowerCase() || '';
      const nameB = b.item?.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }

  // Render the processed and sorted assets.
  return (
    <>
      {processedRooms.map((asset: any, x: number) => {
        if (asset.item === null) {
          return null;
        }

        return (
          <div key={`${asset.id}-${x}`} className="areadocuments__body">
            <AreaDocument
              user={user}
              onDelete={() =>
                window.confirm(
                  `Are you sure you want to delete ${asset.item.name}?`
                ) && onDeleteAsset(viewAssetId, asset.id)
              }
              headline={asset.item && asset.item.name ? asset.item.name : ''}
              onView={() => {
                history.push({
                  pathname: PATHS.ASSETS_VIEW.replace(
                    ':id',
                    viewAssetId + '&' + asset.id
                  ),
                  state: {
                    asset: asset,
                    suite: suite,
                    property: property,
                  },
                });
              }}
              onEdit={() => {
                dispatchSetAssetFormData({
                  ...asset,
                  areaId: viewAssetId,
                  assetWarrantyDefaultStartDate:
                    suite.assetWarrantyDefaultStartDate,
                });
                setShowOverlay(true);
                setNewAsset(false);
              }}
              icon={
                <FontAwesomeIcon
                  glyph={
                    ICONS[
                      asset.item &&
                      asset.item.itemTypeIcons &&
                      asset.item.itemTypeIcons.name
                        ? asset.item.itemTypeIcons.name
                        : ''
                    ]
                  }
                  weight="light"
                />
              }
              active={undefined}
              strapline={undefined}
              itemTypeWalkthrough={undefined}
              small={undefined}
              onImageClicked={undefined}
            />
          </div>
        );
      })}
    </>
  );
};

interface AreaAssetsTableProps {
  assets: any[];
  loader?: boolean;
  onSaveAsset: (asset: any) => void;
  onDeleteAsset: (areaId: string, assetId: string) => void;
  onSearch: (value: string) => void;
  suite: any;
  property: any;
  history: any;
  introContent?: boolean;
}

const AreaAssetsTable: React.FC<AreaAssetsTableProps> = ({
  assets,
  loader: propLoader,
  onSaveAsset,
  onDeleteAsset,
  onSearch,
  suite,
  property,
  history,
  introContent,
}) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [viewAsset, setViewAsset] = useState<string>('');
  const [viewAssetId, setViewAssetId] = useState<string>('');
  const [newAsset, setNewAsset] = useState<boolean>(false);
  const [prevAssetsLength, setPrevAssetsLength] = useState<number>(0);

  // Replace mapStateToProps with useSelector hooks.
  const search = useSelector(selectSearch);
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectFormLoader);

  // Replace mapDispatchToProps with useDispatch hook.
  const dispatch = useDispatch();
  const dispatchSetAssetFormData = (data: any) =>
    dispatch(setAssetFormData(data));
  const dispatchClearAssetFormData = () => dispatch(clearAssetFormData());

  // useEffect equivalent of componentDidMount.
  useEffect(() => {
    openFirstRoom();
  }, []);

  // Track previous assets length.
  useEffect(() => {
    setPrevAssetsLength(assets.length);
  }, [assets.length]);

  // useEffect equivalent of componentDidUpdate.
  useEffect(() => {
    if (assets && assets.length > 0 && assets.length > prevAssetsLength) {
      setViewAsset(viewAsset === assets[0].name ? '' : assets[0].name);
      setViewAssetId(viewAssetId === assets[0].areaId ? '' : assets[0].areaId);
    }
  }, [assets, prevAssetsLength, viewAsset, viewAssetId]);

  const openFirstRoom = () => {
    if (assets && assets.length > 0) {
      setViewAsset(assets[0].name);
      setViewAssetId(assets[0].areaId);
    }
  };

  const expandRoom = (room: any) => {
    setViewAsset(viewAsset === room.name ? '' : room.name);
    setViewAssetId(viewAssetId === room.areaId ? '' : room.areaId);
  };

  const homeOwnerCheck: boolean =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === 'Home Owner' ||
      user.roles[0].role.name === 'Occupant' ||
      user.roles[0].role.name === 'Strata Manager' ||
      user.roles[0].role.name === 'Realtor');

  const handleAddAsset = () => {
    dispatchSetAssetFormData({
      assetWarrantyDefaultStartDate:
        suite && suite.assetWarrantyDefaultStartDate
          ? suite.assetWarrantyDefaultStartDate
          : null,
      dateOfPurchase:
        suite && suite.assetWarrantyDefaultStartDate
          ? suite.assetWarrantyDefaultStartDate
          : null,
    });
    setShowOverlay(true);
    setNewAsset(true);
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    dispatchClearAssetFormData();
  };

  return (
    <>
      {loader ? <Loader center={true} /> : null}
      {introContent && <ContentBlock content={IntroContent.rooms} />}
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Assets..."
          onChange={onSearch}
          theme={undefined}
        />
        {homeOwnerCheck ? null : (
          <Button
            color="teal"
            label="Add Asset"
            onClick={handleAddAsset}
            icon={<FontAwesomeIcon glyph="boxes" weight="light" size="small" />}
            size="small"
          />
        )}
      </Actions>
      {assets.length > 0 ? (
        <div style={{ paddingTop: 15 }}>
          {assets.map((room: any) => (
            <div key={room.name}>
              <div
                style={{
                  display: 'flex',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginLeft: 10,
                  marginRight: 10,
                }}
                onClick={() => expandRoom(room)}
              >
                <div
                  className="service-request-from-asset__title"
                  style={{ cursor: 'pointer', marginRight: 20 }}
                >
                  {room.customLabel ? room.customLabel : room.name}
                </div>
                <div
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: 1,
                  }}
                >
                  <FontAwesomeIcon
                    glyph={
                      viewAsset === room.name ? 'chevron-up' : 'chevron-down'
                    }
                    weight="regular"
                  />
                </div>
              </div>
              <Row style={{ marginLeft: '0px', marginRight: '0px' }}>
                <RoomsAndAssets
                  rooms={room.assets}
                  room={room.name}
                  viewAsset={viewAsset}
                  viewAssetId={viewAssetId}
                  user={user}
                  onDeleteAsset={onDeleteAsset}
                  history={history}
                  suite={suite}
                  property={property}
                  dispatchSetAssetFormData={dispatchSetAssetFormData}
                  setShowOverlay={setShowOverlay}
                  setNewAsset={setNewAsset}
                />
              </Row>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          headline={'This ' + variables.suite + ' is empty'}
          strapline={
            'Add assets to this ' +
            variables.suite +
            ' and attach relevant documentation such as manuals and warranties.'
          }
          searchIsActive={search && search.assets ? search.assets : ''}
          cta={
            homeOwnerCheck ? null : (
              <Button
                color="teal"
                label="Add Asset"
                onClick={handleAddAsset}
                icon={<FontAwesomeIcon glyph="boxes" weight="light" size="small" />}
                size="small"
              />
            )
          }
          image={
            <img
              src="/assets/empty/assets.svg"
              alt={`This ${variables.suite} is empty`}
            />
          }
          direction={undefined}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <AssetForm
            newAsset={newAsset}
            suiteId={suite.id}
            propertyId={suite.propertyId}
            onClose={handleCloseOverlay}
            onSaveAsset={onSaveAsset}
          />
        }
      />
    </>
  );
};

export default AreaAssetsTable;
